/**
 * Strong's word list — shows all words with Strong's numbers
 * from the verse. Each word has two accordion twisters:
 *   - Strong's: original language definition
 *   - Webster's: 1828 English definition (when available)
 * Clicking a Strong's chip opens the full Strong's panel.
 */

import { useState, useCallback } from 'react';
import type { ScriptureWord } from '@skjv/common';
import { bibleState } from '@/state/bible-state';
import { ChevronRightIcon } from '@components/icons';
import { DefinitionText } from '@components/strongs-panel/definition-text';

interface StrongsWordListProps {
  words: ScriptureWord[];
}

interface WordEntry {
  word: string;
  number: string;
  native: string;
  phonetic: string;
  definition: string;
  language: string;
  websterDefinition?: string;
}

function collectWordEntries(words: ScriptureWord[]): WordEntry[] {
  const seen = new Set<string>();
  const entries: WordEntry[] = [];

  for (const w of words) {
    if (w.strongsNumbers.length === 0 || !w.definition) continue;
    const num = w.strongsNumbers[0];
    if (seen.has(num)) continue;
    seen.add(num);
    entries.push({
      word: w.word,
      number: num,
      native: w.definition.native,
      phonetic: w.definition.phonetic,
      definition: w.definition.definition,
      language: w.definition.language,
      websterDefinition: w.websterDefinition,
    });
  }

  return entries;
}

function WordItem({ entry, onChipClick, onLinkClick }: { entry: WordEntry; onChipClick: (num: string, opts?: { englishWord?: string; websterDefinition?: string }) => void; onLinkClick: (num: string) => void }) {
  const [strongsOpen, setStrongsOpen] = useState(false);
  const [websterOpen, setWebsterOpen] = useState(false);

  return (
    <li className="strongs-word-item">
      <div className="strongs-word-header">
        <span className="strongs-word-english">{entry.word}</span>
        <button className="strongs-chip" onClick={() => onChipClick(entry.number, { englishWord: entry.word, websterDefinition: entry.websterDefinition })}>
          {entry.number}
        </button>
        <span className="strongs-word-native">{entry.native}</span>
        <span className="strongs-word-phonetic">{entry.phonetic}</span>
      </div>
      <button
        className="strongs-def-toggle"
        onClick={() => setStrongsOpen(!strongsOpen)}
      >
        <ChevronRightIcon className={`h-3 w-3 strongs-def-chevron ${strongsOpen ? 'strongs-def-chevron-open' : ''}`} />
        <span>Strong&apos;s</span>
      </button>
      {strongsOpen && (
        <p className="strongs-word-def">
          <DefinitionText
            definition={entry.definition}
            language={entry.language}
            onLinkClick={onLinkClick}
          />
        </p>
      )}
      {entry.websterDefinition && (
        <>
          <button
            className="webster-def-toggle"
            onClick={() => setWebsterOpen(!websterOpen)}
          >
            <ChevronRightIcon className={`h-3 w-3 webster-def-chevron ${websterOpen ? 'webster-def-chevron-open' : ''}`} />
            <span>Webster&apos;s</span>
          </button>
          {websterOpen && (
            <p className="webster-word-def">
              {entry.websterDefinition.length > 300
                ? entry.websterDefinition.substring(0, 300) + '...'
                : entry.websterDefinition}
            </p>
          )}
        </>
      )}
    </li>
  );
}

export function StrongsWordList({ words }: StrongsWordListProps) {
  const [open, setOpen] = useState(false);
  const entries = collectWordEntries(words);

  const handleClick = useCallback((num: string, opts?: { englishWord?: string; websterDefinition?: string }) => {
    bibleState.showStrongsDefinition(num, bibleState.currentLocation, opts);
  }, []);

  if (entries.length === 0) return null;

  return (
    <div className="rich-message-section">
      <h4 className="rich-message-section-title">Word Study</h4>
      <button className="word-study-toggle" onClick={() => setOpen(!open)}>
        <ChevronRightIcon className={`h-3 w-3 word-study-chevron ${open ? 'word-study-chevron-open' : ''}`} />
        <span>View {entries.length} definitions...</span>
      </button>
      {open && (
        <ul className="strongs-word-list">
          {entries.map((entry) => (
            <WordItem key={entry.number} entry={entry} onChipClick={handleClick} onLinkClick={handleClick} />
          ))}
        </ul>
      )}
    </div>
  );
}
